const COOKIE_NAME = "gb_id";
const COOKIE_MAX_AGE_MS = 730 * 24 * 60 * 60 * 1000;
const VALUE_WHITELIST = /^[A-Za-z0-9._-]{1,64}$/;
const GA_REGEX = /(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/;
const GB_REGEX = /(?:^|;\s*)gb_id=([^;]+)/;

export type GrowthBookIdSource = "ga" | "gb_id" | "fresh" | "blocked" | "ssr";
export interface GrowthBookIdResult {
  id: string | null;
  source: GrowthBookIdSource;
}

let memoized: GrowthBookIdResult | null = null;

/**
 * Resolve a stable anonymous ID for GrowthBook bucketing, matching the priority used
 * by the WP marketing site plugin (growth-engine/modules/wordpress/plugins/speakai-growthbook.php):
 * 1. `_ga` cookie numeric tail (joins to GA4 user_pseudo_id in BQ)
 * 2. `gb_id` first-party cookie value
 * 3. Fresh random + seed `gb_id` cookie on `.speakai.co` (or host-only on non-speakai.co hosts)
 *
 * Idempotent within a session: memoized after first call, never re-seeds.
 * SSR-safe: returns `{id: null, source: "ssr"}` if `document` is undefined.
 *
 * Cookie values are validated against /^[A-Za-z0-9._-]{1,64}$/ — malformed/tampered
 * values fall through to fresh generation.
 */
export function resolveGrowthBookClientId(): GrowthBookIdResult {
  if (memoized) return memoized;
  if (typeof document === "undefined") {
    return { id: null, source: "ssr" };
  }

  const cookieStr = document.cookie;

  const gaMatch = cookieStr.match(GA_REGEX);
  if (gaMatch && VALUE_WHITELIST.test(gaMatch[1])) {
    memoized = { id: gaMatch[1], source: "ga" };
    return memoized;
  }

  const gbMatch = cookieStr.match(GB_REGEX);
  if (gbMatch && VALUE_WHITELIST.test(gbMatch[1])) {
    memoized = { id: gbMatch[1], source: "gb_id" };
    return memoized;
  }

  // Match WP plugin generator exactly: Math.random base36 (9 chars) + '.' + Date.now base36
  const fresh = Math.random().toString(36).slice(2, 11) + "." + Date.now().toString(36);
  writeGbIdCookie(fresh);

  // Verify the write actually persisted. Cookie-blocked browsers (strict enterprise
  // configs, hard cookie-block extensions, some Safari ITP edge cases) silently reject
  // document.cookie assignments. Detecting this lets us return a deterministic null id
  // so downstream `if (gbClientId)` guards skip setAttributes — Phase E pricing
  // experiments using hashAttribute: "gb_client_id" then fail to bucket the user and
  // fall through to defaultValue (= control). No variant flicker, clean readout.
  // Regular incognito (cookies work within session) passes this check.
  if (typeof document !== "undefined" && !document.cookie.includes(`${COOKIE_NAME}=${fresh}`)) {
    memoized = { id: null, source: "blocked" };
    return memoized;
  }

  memoized = { id: fresh, source: "fresh" };
  return memoized;
}

/** Memoized getter — returns id only (null on SSR or pre-resolve). */
export function getGrowthBookClientId(): string | null {
  return resolveGrowthBookClientId().id;
}

// Tests reset memoization via vi.resetModules() + dynamic re-import (canonical Vitest pattern).
// No production reset export — public surface stays at 2 named functions.

function writeGbIdCookie(value: string): void {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + COOKIE_MAX_AGE_MS).toUTCString();
  const isSpeakaiHost =
    typeof window !== "undefined" && window.location.hostname.endsWith(".speakai.co");
  const isHttps =
    typeof window !== "undefined" && window.location.protocol === "https:";

  const parts = [
    `${COOKIE_NAME}=${value}`,
    `expires=${expires}`,
    "Path=/",
    "SameSite=Lax",
  ];
  if (isSpeakaiHost) parts.push("Domain=.speakai.co");
  if (isHttps) parts.push("Secure");
  document.cookie = parts.join("; ");
}
